import { eapQualityReportQuery } from "@/api/reporting";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { StyledLink } from "@/components/link/StyledLink";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Box, QueryHandler } from "node_modules/@givenwell/components/src";
import { EapQualityReportItem } from "node_modules/@givenwell/management-api";

export const Route = createFileRoute("/admin/reports/$countryCode/eap-quality")({
  component: EapListingQualityReportPage,
  beforeLoad: () => ({
    breadcrumb: "EAP Listing quality",
  }),
});

function EapListingQualityReportPage() {
  const countryCode = Route.useParams().countryCode;
  const eapRequestsQuery = useQuery(eapQualityReportQuery(countryCode));

  return (
    <PageWrapper title="EAP Listing quality">
      <Box css={{ spaceY: 16, mt: 16 }}>
        <QueryHandler
          query={eapRequestsQuery}
          success={({ data }) => (
            <EapListingQualityReportTable records={data.listing_qualities || []} />
          )}
        />
      </Box>
    </PageWrapper>
  );
}

interface EapSessionsTableProps {
  records: EapQualityReportItem[];
}

function EapListingQualityReportTable({ records }: EapSessionsTableProps) {
  return (
    <DataTable>
      <DataTableBody
        rounded="full"
        data={records}
        columns={[
          {
            header: "Supplier",
            cell: (r) => (
              <StyledLink
                to={`/admin/suppliers/$supplierId`}
                params={{ supplierId: r.supplier_id }}
              >
                {r.supplier_name}
              </StyledLink>
            ),
            width: "350px",
          },
          {
            header: "Listing",
            cell: (r) => (
              <StyledLink
                to={`/supplier/$supplierId/eap/$listingId/$version`}
                params={{
                  supplierId: r.supplier_id,
                  listingId: r.listing_id,
                  version: r.listing_version,
                }}
              >
                {r.listing_title}
              </StyledLink>
            ),
            width: "250px",
          },
          {
            header: "Markets",
            cell: (r) =>
              r.markets.map((market) => (
                <span key={market} style={GetMarketStyle(market)}>
                  {market}
                </span>
              )),
          },
          {
            header: "Instant Booking",
            cell: (r) =>
              r.is_instant_booking ? (
                <span style={{ color: "green" }}>✅</span>
              ) : (
                <span style={{ color: "red" }}>❌</span>
              ),
          },
          {
            header: "Connected Calendars",
            cell: (r) =>
              r.has_connected_calendars ? (
                <span style={{ color: "green" }}>✅</span>
              ) : (
                <span style={{ color: "red" }}>❌</span>
              ),
          },
          {
            header: "Valid Registration",
            cell: (r) =>
              r.has_valid_registration ? (
                <span style={{ color: "green" }}>✅</span>
              ) : (
                <span style={{ color: "red" }}>❌</span>
              ),
          },
          {
            header: "Days Since Last Sale",
            cell: (r) =>
              r.days_since_last_sale !== null && r.days_since_last_sale !== undefined
                ? r.days_since_last_sale
                : "N/A",
          },
          {
            header: "Publish Date",
            cell: (r) => (r.approved_date ? new Date(r.approved_date).toLocaleDateString() : "N/A"),
          },
          {
            header: "Issues",
            cell: (r) =>
              r.issues.length > 0 ? (
                <Dialog
                  title={`${r.listing_title} - Listing Issues`}
                  css={{ maxWidth: 800 }}
                  content={
                    <ul style={{ listStyleType: "disc", paddingLeft: "20px" }}>
                      {r.issues.map((issue, index) => (
                        <li key={index} style={{ paddingTop: "4px" }}>
                          {issue.message}
                        </li>
                      ))}
                    </ul>
                  }
                >
                  <Button variant="outlined" size="xs">
                    View Issues
                  </Button>
                </Dialog>
              ) : (
                ""
              ),
          },
        ]}
        dataKey={(r) => r.listing_id}
      />
    </DataTable>
  );

  function GetMarketStyle(market: string): React.CSSProperties {
    const commonStyle: React.CSSProperties = {
      padding: "2px 6px",
      borderRadius: "4px",
      marginRight: "4px",
    };
    switch (market) {
      case "nz":
        return { backgroundColor: "#000", color: "#fff", ...commonStyle };
      case "au":
        return { backgroundColor: "#00843D", color: "#FFCD00", ...commonStyle };
      default:
        return { backgroundColor: "#E0E0E0", color: "#000", ...commonStyle };
    }
  }
}
